import { RobotApi } from 'api/RobotApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import CommonButton from 'components/Button/ButtonTypes/CommonButton'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { ReactComponent as CrossSvg } from 'images/icons/cross.svg'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import { IRobotInfo } from 'utils/interfaces'

import style from './AddRobotsModal.module.css'

interface IAddRobotModal {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  className?: string
  robotExisting?: IRobotInfo
  robots: IRobotInfo[]
}

const AddRobotModal: React.FC<IAddRobotModal> = props => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState<string>(props.robotExisting?.name || t('robots.new_robot_name'))
  const [description, setDescription] = useState(props.robotExisting?.description || '')
  const [serialId, setSerialId] = useState(
    props.robotExisting ? props.robotExisting?.serialNumber : Math.max(...props.robots.map(r => r.serialNumber)) + 1
  )

  const onSaveClick = async () => {
    try {
      setSaving(true)

      if (props.robotExisting) {
        await RobotApi.patchRobot(props.robotExisting.id, name, description)
      } else {
        await RobotApi.createRobot(name, description, serialId)
      }
      props.onSave()
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.log('Error creating robot')
    }
    setSaving(false)
  }

  const serialIdError = useMemo(() => {
    return !props.robotExisting && (!serialId || !!props.robots.find(robot => robot.serialNumber === serialId))
  }, [props.robotExisting, props.robots, serialId])

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{props.robotExisting ? t('robots.edit_existing') : t('robots.add_new')}</div>
        <CommonButton className={style.btnClose} onClick={props.onClose} disabled={false}>
          <CrossSvg />
        </CommonButton>
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_name')}
          defaultValue={name}
          placeholder={t('robots.robot_name_placeholder')}
          onChange={e => setName(e.target.value)}
        />
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_serial_id')}
          defaultValue={String(serialId)}
          placeholder={t('robots.robot_serial_id_placeholder')}
          onChange={e => setSerialId(parseFloat(e.target.value))}
          disabled={!!props.robotExisting}
          type="number"
          error="Serial ID must be unique"
          hasError={serialIdError}
        />
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_description')}
          defaultValue={description}
          placeholder={t('robots.robot_description_placeholder')}
          onChange={e => setDescription(e.target.value)}
        />
      </div>

      <div className={style.footer}>
        <Button.Outlined className={style.btn} disabled={saving} onClick={props.onClose}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving}
          loading={saving}
          onClick={onSaveClick}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default AddRobotModal
